import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useSuperAdmin } from "@/hooks/useSuperAdmin";
import {
  listSponsorCampaigns,
  upsertSponsorCampaign,
  deleteSponsorCampaign,
  type SponsorCampaignRow,
} from "@/lib/sponsorPredictions";

export const Route = createFileRoute("/admin/sponsors")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Sponsors · VerseSport Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: SponsorsAdminPage,
});

type Draft = Partial<SponsorCampaignRow>;

function emptyDraft(): Draft {
  return {
    brand_name: "",
    logo_url: "",
    banner_url: "",
    question: "",
    reward_xp: 100,
    country_scope: [],
    language_scope: [],
    device_scope: [],
    priority: 10,
    frequency_minutes: 20,
    active: true,
    start_date: new Date().toISOString(),
    end_date: null,
  };
}

function csvToArray(s: string): string[] {
  return s.split(",").map((x) => x.trim()).filter(Boolean);
}

function SponsorsAdminPage() {
  const { isSuperAdmin, isLoading } = useSuperAdmin();
  const [rows, setRows] = useState<SponsorCampaignRow[]>([]);
  const [draft, setDraft] = useState<Draft>(emptyDraft());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const refresh = async () => {
    try { setRows(await listSponsorCampaigns()); }
    catch (e) { setErr((e as Error).message); }
  };

  useEffect(() => { if (isSuperAdmin) void refresh(); }, [isSuperAdmin]);

  if (isLoading) return <main className="p-4 text-sm">Verificando acceso…</main>;
  if (!isSuperAdmin) {
    return (
      <main className="mx-auto max-w-md p-4 text-sm">
        <p className="font-bold">Acceso restringido</p>
        <Link to="/" className="text-primary underline">Volver</Link>
      </main>
    );
  }

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      await upsertSponsorCampaign(draft);
      setDraft(emptyDraft());
      await refresh();
    } catch (e) { setErr((e as Error).message); }
    finally { setSaving(false); }
  };

  const toggleActive = async (r: SponsorCampaignRow) => {
    await upsertSponsorCampaign({ ...r, active: !r.active });
    await refresh();
  };

  const remove = async (id: string) => {
    if (!confirm("¿Eliminar esta campaña patrocinada?")) return;
    await deleteSponsorCampaign(id);
    await refresh();
  };

  return (
    <main className="mx-auto max-w-3xl space-y-4 px-3 py-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-black">🤝 Sponsor Campaigns</h1>
          <p className="text-[12px] text-muted-foreground">PR-28 · Infraestructura comercial — sin pagos ni apuestas.</p>
        </div>
        <Link to="/admin/sponsor-analytics" className="rounded-lg border border-border bg-background px-2 py-1 text-[11px] font-bold hover:bg-accent">
          Ver analytics →
        </Link>
      </header>

      <section className="rounded-xl border border-border bg-background/60 p-3">
        <h2 className="text-sm font-black">{draft.id ? "Editar" : "Nueva"} campaña</h2>
        {err && <p className="mt-2 rounded bg-destructive/10 p-2 text-[11px] text-destructive">{err}</p>}
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <label className="text-[11px] font-bold">Marca
            <input className="mt-0.5 w-full rounded border border-border bg-background px-2 py-1 text-[12px]"
              value={draft.brand_name ?? ""} onChange={(e) => setDraft({ ...draft, brand_name: e.target.value })} />
          </label>
          <label className="text-[11px] font-bold">Reward XP
            <input type="number" className="mt-0.5 w-full rounded border border-border bg-background px-2 py-1 text-[12px]"
              value={draft.reward_xp ?? 100} onChange={(e) => setDraft({ ...draft, reward_xp: Number(e.target.value) })} />
          </label>
          <label className="text-[11px] font-bold sm:col-span-2">Pregunta
            <input className="mt-0.5 w-full rounded border border-border bg-background px-2 py-1 text-[12px]"
              value={draft.question ?? ""} onChange={(e) => setDraft({ ...draft, question: e.target.value })} />
          </label>
          <label className="text-[11px] font-bold sm:col-span-2">Logo URL
            <input className="mt-0.5 w-full rounded border border-border bg-background px-2 py-1 text-[12px]"
              value={draft.logo_url ?? ""} onChange={(e) => setDraft({ ...draft, logo_url: e.target.value })} />
          </label>
          <label className="text-[11px] font-bold sm:col-span-2">Banner URL
            <input className="mt-0.5 w-full rounded border border-border bg-background px-2 py-1 text-[12px]"
              value={draft.banner_url ?? ""} onChange={(e) => setDraft({ ...draft, banner_url: e.target.value })} />
          </label>
          <label className="text-[11px] font-bold">Países (ISO, coma)
            <input className="mt-0.5 w-full rounded border border-border bg-background px-2 py-1 text-[12px]"
              value={(draft.country_scope ?? []).join(",")}
              onChange={(e) => setDraft({ ...draft, country_scope: csvToArray(e.target.value) })} placeholder="MX,US,BR" />
          </label>
          <label className="text-[11px] font-bold">Idiomas (coma)
            <input className="mt-0.5 w-full rounded border border-border bg-background px-2 py-1 text-[12px]"
              value={(draft.language_scope ?? []).join(",")}
              onChange={(e) => setDraft({ ...draft, language_scope: csvToArray(e.target.value) })} placeholder="ES,EN,PT" />
          </label>
          <label className="text-[11px] font-bold">Device (mobile,desktop)
            <input className="mt-0.5 w-full rounded border border-border bg-background px-2 py-1 text-[12px]"
              value={(draft.device_scope ?? []).join(",")}
              onChange={(e) => setDraft({ ...draft, device_scope: csvToArray(e.target.value) })} placeholder="mobile,desktop" />
          </label>
          <label className="text-[11px] font-bold">Prioridad
            <input type="number" className="mt-0.5 w-full rounded border border-border bg-background px-2 py-1 text-[12px]"
              value={draft.priority ?? 10} onChange={(e) => setDraft({ ...draft, priority: Number(e.target.value) })} />
          </label>
          <label className="text-[11px] font-bold">Inicio
            <input type="datetime-local" className="mt-0.5 w-full rounded border border-border bg-background px-2 py-1 text-[12px]"
              value={draft.start_date ? new Date(draft.start_date).toISOString().slice(0,16) : ""}
              onChange={(e) => setDraft({ ...draft, start_date: new Date(e.target.value).toISOString() })} />
          </label>
          <label className="text-[11px] font-bold">Fin
            <input type="datetime-local" className="mt-0.5 w-full rounded border border-border bg-background px-2 py-1 text-[12px]"
              value={draft.end_date ? new Date(draft.end_date).toISOString().slice(0,16) : ""}
              onChange={(e) => setDraft({ ...draft, end_date: e.target.value ? new Date(e.target.value).toISOString() : null })} />
          </label>
          <label className="flex items-center gap-2 text-[11px] font-bold sm:col-span-2">
            <input type="checkbox" checked={draft.active ?? true}
              onChange={(e) => setDraft({ ...draft, active: e.target.checked })} />
            Activa
          </label>
        </div>
        <div className="mt-3 flex gap-2">
          <button disabled={saving} onClick={save}
            className="rounded-lg bg-primary px-3 py-1.5 text-[12px] font-black text-primary-foreground disabled:opacity-50">
            {saving ? "Guardando…" : draft.id ? "Actualizar" : "Crear"}
          </button>
          {draft.id && (
            <button onClick={() => setDraft(emptyDraft())} className="rounded-lg border border-border px-3 py-1.5 text-[12px] font-bold">
              Cancelar
            </button>
          )}
        </div>
      </section>

      <section className="rounded-xl border border-border bg-background/60 p-3">
        <h2 className="text-sm font-black">Campañas ({rows.length})</h2>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-left text-[11px]">
            <thead className="text-muted-foreground">
              <tr>
                <th className="px-2 py-1">Marca</th>
                <th className="px-2 py-1">Pregunta</th>
                <th className="px-2 py-1">XP</th>
                <th className="px-2 py-1">País</th>
                <th className="px-2 py-1">Activa</th>
                <th className="px-2 py-1"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border/40">
                  <td className="px-2 py-1 font-black">{r.brand_name}</td>
                  <td className="px-2 py-1">{r.question}</td>
                  <td className="px-2 py-1 tabular-nums">{r.reward_xp}</td>
                  <td className="px-2 py-1 text-[10px] text-muted-foreground">{r.country_scope.join(",") || "all"}</td>
                  <td className="px-2 py-1">
                    <button onClick={() => toggleActive(r)} className={`rounded px-1.5 py-0.5 text-[10px] font-black ${r.active ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"}`}>
                      {r.active ? "ON" : "OFF"}
                    </button>
                  </td>
                  <td className="px-2 py-1">
                    <div className="flex gap-1">
                      <button onClick={() => setDraft(r)} className="rounded border border-border px-1.5 py-0.5 text-[10px] hover:bg-accent">Editar</button>
                      <button onClick={() => remove(r.id)} className="rounded border border-destructive/40 px-1.5 py-0.5 text-[10px] text-destructive hover:bg-destructive/10">Borrar</button>
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="px-2 py-4 text-center text-muted-foreground">Sin campañas. Crea la primera ↑</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}